import { useEffect } from 'react';
import {
  buildCountryOptionKey,
  countryToPhoneFields,
  findCountryByEnName,
  findCountryByOptionKey,
  formatCountryOptionLabel,
} from '@formosa/shared/utils/countryUtils';
import { useTravelText } from '../app/TravelLocaleContext';
import { useCountryOptions } from './useCountryOptions';

export default function PhoneCountryFields({
  countryKey = '',
  countryEnName = '',
  phoneAreaCode = '',
  onChange,
  allowedEnNames,
  disabled = false,
  idPrefix = 'travel-phone',
}) {
  const { countries, loading, defaultCountry } = useCountryOptions(allowedEnNames);
  const { t } = useTravelText();

  useEffect(() => {
    if (loading || countries.length === 0 || !onChange) return;

    if (countryKey) {
      const selected = findCountryByOptionKey(countries, countryKey);
      if (selected) return;
    }

    if (countryEnName) {
      const legacy =
        findCountryByEnName(countries, countryEnName, phoneAreaCode)
        || findCountryByEnName(countries, countryEnName);
      if (legacy) {
        const next = countryToPhoneFields(legacy);
        if (next.countryKey !== countryKey || next.phoneAreaCode !== phoneAreaCode) {
          onChange(next);
        }
        return;
      }
    }

    if (!countryKey && !countryEnName && defaultCountry) {
      onChange(countryToPhoneFields(defaultCountry));
    }
  }, [
    loading,
    countries,
    countryKey,
    countryEnName,
    phoneAreaCode,
    defaultCountry,
    onChange,
  ]);

  function handleCountrySelect(event) {
    const selected = findCountryByOptionKey(countries, event.target.value);
    if (!selected) {
      onChange?.({ countryKey: '', countryEnName: '', phoneAreaCode: '' });
      return;
    }
    onChange?.(countryToPhoneFields(selected));
  }

  const selectValue =
    countryKey
    || buildCountryOptionKey(findCountryByEnName(countries, countryEnName, phoneAreaCode))
    || '';

  return (
    <div className="travel-auth__phone-row">
      <label className="travel-auth__field travel-auth__field--grow">
        <span>{t('travel.auth.phone.country')}</span>
        <select
          id={`${idPrefix}-country`}
          value={selectValue}
          onChange={handleCountrySelect}
          disabled={disabled || loading || countries.length === 0}
          required
        >
          <option value="">{t(loading ? 'travel.auth.phone.loading' : 'travel.auth.phone.select')}</option>
          {countries.map((item) => (
            <option key={buildCountryOptionKey(item)} value={buildCountryOptionKey(item)}>
              {formatCountryOptionLabel(item)}
            </option>
          ))}
        </select>
      </label>
      <label className="travel-auth__field travel-auth__field--area">
        <span>{t('travel.auth.phone.code')}</span>
        <input
          id={`${idPrefix}-area-code`}
          type="text"
          value={phoneAreaCode}
          readOnly
          tabIndex={-1}
          aria-label={t('travel.auth.phone.codeLabel')}
        />
      </label>
    </div>
  );
}
